import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "../../css/Dashboard.module.css";
import { useAuth } from "../../contexts/AuthContext";
import Chatbot from "../SharedComponents/Chatbot";

const Dashboard = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [requirements, setRequirements] = useState("");
  const [jobType, setJobType] = useState("");
  const [jobs, setJobs] = useState([]);
  const [preferredJobs, setPreferredJobs] = useState([]);
  const [resume, setResume] = useState(null); // State for resume file
  const [resumeTitle, setResumeTitle] = useState(""); // State to store resume title
  const [sortField, setSortField] = useState("createdAt"); // Default sorting field
  const [sortOrder, setSortOrder] = useState("desc"); // Default sorting order
  const [error, setError] = useState(""); // State for handling errors
  const [loading, setLoading] = useState(true); // Loading state for the component
  const [userUniversity, setUserUniversity] = useState(null); // State to store user's university
  const [userDetails, setUserDetails] = useState(null);
  const navigate = useNavigate();
  const { userRole } = useAuth();

  const saveSearch = (queryParams) => {
    const searches = JSON.parse(localStorage.getItem("searches")) || [];
    const newSearch = {
      title: queryParams.get("title") || "",
      company: queryParams.get("company") || "",
      location: queryParams.get("location") || "",
      requirements: queryParams.get("requirements") || "",
      jobType: queryParams.get("jobType") || "",
    };

    // Check if all properties are empty
    const isEmptySearch = Object.values(newSearch).every(
      (value) => value === ""
    );

    // Check for duplicate search
    const isDuplicateSearch = searches.some(
      (search) =>
        search.title === newSearch.title &&
        search.company === newSearch.company &&
        search.location === newSearch.location &&
        search.requirements === newSearch.requirements &&
        search.jobType === newSearch.jobType
    );

    if (!isEmptySearch && !isDuplicateSearch) {
      searches.push(newSearch);
      if (searches.length > 3) {
        searches.shift(); // Keep only the last 3 searches
      }
      localStorage.setItem("searches", JSON.stringify(searches));
    }
  };

  const filterJobs = (jobs, queryParams) => {
    return jobs.filter((job) => {
      if (
        queryParams.get("title") &&
        !job.title
          .toLowerCase()
          .includes(queryParams.get("title").toLowerCase())
      )
        return false;
      if (
        queryParams.get("company") &&
        !job.company
          .toLowerCase()
          .includes(queryParams.get("company").toLowerCase())
      )
        return false;
      if (
        queryParams.get("location") &&
        !job.location
          .toLowerCase()
          .includes(queryParams.get("location").toLowerCase())
      )
        return false;

      if (queryParams.get("requirements")) {
        const requiredSkills = queryParams
          .get("requirements")
          .toLowerCase()
          .split(",")
          .map((skill) => skill.trim());
        const jobRequirements = Array.isArray(job.requirements)
          ? job.requirements.map((skill) => skill.toLowerCase().trim())
          : [];
        if (!requiredSkills.every((skill) => jobRequirements.includes(skill)))
          return false;
      }

      if (
        queryParams.get("jobType") &&
        job.jobType.toLowerCase() !== queryParams.get("jobType").toLowerCase()
      )
        return false;
      return true;
    });
  };

  useEffect(() => {
    // Redirect based on user role
    if (userRole === 'recruiter') {
      navigate('/recruiter/dashboard');
      return;
    } else if (userRole === 'universitypartner') {
      navigate('/university/dashboard');
      return;
    }

    const fetchUserDetails = async () => {
      try {
        const response = await axios.get('https://techservicemarket-backend.onrender.com/api/users/profile', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setUserDetails(response.data);
        setUserUniversity(response.data.university); // Set user's university
      } catch (error) {
        console.error('Error fetching user details:', error);
        // Don't show the error for recruiter/university roles
        if (userRole === 'user') {
          setError('Error fetching user details');
        }
      }
    };

    const fetchJobs = async () => {
      try {
        const response = await axios.get(
          'https://techservicemarket-backend.onrender.com/api/users/jobs',
          {
            params: {
              sortField: 'createdAt',
              sortOrder: 'desc',
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        setJobs(response.data);
      } catch (error) {
        console.error('Error fetching jobs:', error);
        // Don't show the error for recruiter/university roles
        if (userRole === 'user') {
          setError('Error fetching jobs');
        }
      } finally {
        setLoading(false);
      }
    };

    if (userRole === 'user') {
      fetchUserDetails();
      fetchJobs();
    }
  }, [navigate, userRole]);

  // If not a regular user, don't render anything (will redirect)
  if (userRole !== 'user') {
    return <div className={styles.loading}>Redirecting...</div>;
  }

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError("");
      const queryParams = new URLSearchParams();
      if (searchTerm) queryParams.append("title", searchTerm);
      if (company) queryParams.append("company", company);
      if (location) queryParams.append("location", location);
      if (requirements) queryParams.append("requirements", requirements);
      if (jobType) queryParams.append("jobType", jobType);
      if (sortField) queryParams.append("sortField", sortField);
      if (sortOrder) queryParams.append("sortOrder", sortOrder);

      const token = localStorage.getItem("token");
      const response = await axios.get(
        `https://techservicemarket-backend.onrender.com/api/users/jobs?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      saveSearch(queryParams);
      setJobs(response.data); // Updated list will exclude reposted jobs
      getPreferredJobs();
    } catch (error) {
      console.error("Error fetching jobs:", error);
      setError("An error occurred while fetching jobs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getPreferredJobs = async () => {
    const searches = JSON.parse(localStorage.getItem("searches")) || [];
    if (searches.length < 3) {
      setPreferredJobs([]);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("You need to log in first.");
        navigate("/university/login");
        return;
      }

      const fetchJobs = async (search) => {
        const queryParams = new URLSearchParams();
        if (search.title) queryParams.append("title", search.title);
        if (search.company) queryParams.append("company", search.company);
        if (search.location) queryParams.append("location", search.location);
        if (search.requirements)
          queryParams.append("requirements", search.requirements);
        if (search.jobType) queryParams.append("jobType", search.jobType);

        const response = await axios.get(
          `https://techservicemarket-backend.onrender.com/api/users/jobs?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const filteredJobs = filterJobs(response.data, queryParams);
        return filteredJobs;
      };

      const [jobs1, jobs2, jobs3] = await Promise.all([
        fetchJobs(searches[0]),
        fetchJobs(searches[1]),
        fetchJobs(searches[2]),
      ]);

      const allJobs = [...jobs1, ...jobs2, ...jobs3];
      const uniqueJobs = Array.from(new Set(allJobs.map((job) => job._id))).map(
        (id) => allJobs.find((job) => job._id === id)
      );
      setPreferredJobs(uniqueJobs);
    } catch (error) {
      console.error("Error fetching preferred jobs:", error);
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    setResume(file);

    const formData = new FormData();
    formData.append("resume-upload", file);

    try {
      const response = await axios.post(
        "https://techservicemarket-backend.onrender.com/api/users/profile/resume",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Resume uploaded successfully:", response.data);
      setResumeTitle(response.data.file); // Assuming backend returns filename
    } catch (error) {
      console.error("Error uploading resume:", error);
    }
  };

  const handleSortChange = (field) => {
    // Toggle sort order if the same field is selected, otherwise reset to ascending
    if (sortField === field) {
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const highlightSkills = (jobSkills = []) => {
    const skillList = requirements
      ? requirements
          .split(",")
          .map((skill) => skill.trim().toLowerCase())
          .filter((skill) => skill !== "")
      : [];

    return jobSkills.map((skill) => {
      const isExactMatch = skillList.includes(skill.toLowerCase());
      return (
        <span key={skill} className={isExactMatch ? styles.highlight : ""}>
          {skill}
        </span>
      );
    });
  };

  return (
    <div className={styles.dashboardContainer}>
      <h1 className={styles.dashboardHeader}>Job Search</h1>
      <div className={styles.contentContainer}>
        {/* Search Section */}
        <div className={styles.searchSection}>
          <input
            type="text"
            placeholder="Search by Title"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          <input
            type="text"
            placeholder="Search by Company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className={styles.searchInput}
          />
          <input
            type="text"
            placeholder="Search by Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className={styles.searchInput}
          />
          <input
            type="text"
            placeholder="Search by Requirements"
            value={requirements}
            onChange={(e) => setRequirements(e.target.value)}
            className={styles.searchInput}
          />
          <select
            value={jobType}
            onChange={(e) => setJobType(e.target.value)}
            className={styles.selectInput}
          >
            <option value="">Select Job Type</option>
            <option value="Full-time">Full-time</option>
            <option value="Part-time">Part-time</option>
            <option value="Contract">Contract</option>
            <option value="Internship">Internship</option>
          </select>
          <button onClick={handleSearch} className={styles.searchButton}>
            Search
          </button>

          {/* Upload Resume Button */}
          <label htmlFor="resume-upload" className={styles.uploadButton}>
            Upload Resume
          </label>
          <input
            type="file"
            id="resume-upload"
            onChange={handleResumeUpload}
            style={{ display: "none" }}
          />
        </div>

        {/* Sorting Options and Navigate Button */}
        <div className={styles.sortSection}>
          <div className="sort-buttons">
            <button
              onClick={() => handleSortChange("salary")}
              className={styles.sortButton}
            >
              Sort by Salary{" "}
              {sortField === "salary" &&
                (sortOrder === "asc" ? "(less)" : "(more)")}
            </button>
            <button
              onClick={() => handleSortChange("requirements")}
              className={styles.sortButton}
            >
              Sort by Requirements{" "}
              {sortField === "requirements" &&
                (sortOrder === "asc" ? "(less)" : "(more)")}
            </button>
            <button
              onClick={() => handleSortChange("createdAt")}
              className={styles.sortButton}
            >
              Sort by Date Posted{" "}
              {sortField === "createdAt" &&
                (sortOrder === "asc" ? "(old to new)" : "(new to old)")}
            </button>
          </div>
          {userUniversity && ( // Conditionally render the button
            <button
              onClick={() => navigate("/dashboard/university-posts")}
              className={styles.searchButton}
            >
              University Jobs
            </button>
          )}
          {userUniversity && ( // Conditionally render the button
            <button
              onClick={() => navigate("/dashboard/preferredJobs")}
              className={styles.searchButton}
            >
              Preferred Jobs
            </button>
          )}
        </div>

        {/* Job List Section */}
        <div className={styles.jobListSection}>
          {loading && <p>Loading jobs...</p>}

          {!loading && error && <p className={styles.errorText}>{error}</p>}

          {!loading && !error && jobs.length === 0 && (
            <p>No jobs found. Try adjusting your search criteria.</p>
          )}

          {/* Preferred Jobs Section */}
          {!loading && !error && preferredJobs.length > 0 && (
            <div>
              <h2>Preferred Jobs</h2>
              {preferredJobs.map((job) => (
                <Link
                  to={`jobs/${job._id}`}
                  key={job._id}
                  className={styles.jobCardLink}
                >
                  <div className={styles.jobCard}>
                    <h3>{job.title}</h3>
                    <p>
                      <strong>Company:</strong> {job.company || "Unknown"}
                    </p>
                    <p>
                      <strong>Location:</strong>{" "}
                      {job.location || "Not specified"}
                    </p>
                    <p>
                      <strong>Salary:</strong>{" "}
                      {job.salary ? `$${job.salary}` : "Not specified"}
                    </p>
                    <p>
                      <strong>Job Type:</strong>{" "}
                      {job.jobType || "Not specified"}
                    </p>
                    <p>
                      <strong>Skills Required:</strong>{" "}
                      {highlightSkills(job.requirements || [])}
                    </p>
                    <p>
                      <strong>Date Posted:</strong>{" "}
                      {new Date(job.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {/* Jobs Section */}
          {<h2>All jobs</h2>}
          {!loading &&
            !error &&
            jobs.length > 0 &&
            jobs.map((job) => (
              <Link
                to={`jobs/${job._id}`}
                key={job._id}
                className={styles.jobCardLink}
              >
                <div className={styles.jobCard}>
                  <h3>{job.title}</h3>
                  <p>
                    <strong>Company:</strong> {job.company || "Unknown"}
                  </p>
                  <p>
                    <strong>Location:</strong> {job.location || "Not specified"}
                  </p>
                  <p>
                    <strong>Salary:</strong>{" "}
                    {job.salary ? `$${job.salary}` : "Not specified"}
                  </p>
                  <p>
                    <strong>Job Type:</strong> {job.jobType || "Not specified"}
                  </p>
                  <p>
                    <strong>Skills Required:</strong>{" "}
                    {highlightSkills(job.requirements || [])}
                  </p>
                  <p>
                    <strong>Date Posted:</strong>{" "}
                    {new Date(job.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </Link>
            ))}
        </div>
      </div>

      <Chatbot />
    </div>
  );
};

export default Dashboard;
